import {
    Button,
    Heading,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
} from '@chakra-ui/react';

/**
 * Modal reutilizável com controle de abrir/fechar interno.
 *
 * @param {function} trigger  - Render prop: ({ onOpen }) => ReactNode
 *                              O componente retornado é responsável por abrir o modal.
 * @param {string}   title    - Título exibido no cabeçalho do modal.
 * @param {ReactNode|function} children - Conteúdo do corpo do modal.
 *                              Aceita render prop: ({ onClose }) => ReactNode
 *                              para fechar o modal ao submeter o formulário.
 * @param {string}   [size]   - Tamanho do modal (padrão: "lg").
 * @param {string}   [formId] - ID de um <form> no children para associar o botão Salvar.
 *                              Se omitido, o rodapé só exibe o botão Fechar.
 * @param {function} [onClose] - Callback extra chamado ao fechar o modal.
 *
 * @example
 * <ModalCore
 *   trigger={({ onOpen }) => <Button onClick={onOpen}>Novo Lead</Button>}
 *   title="Criar Lead"
 *   formId="form-lead"
 * >
 *   <form id="form-lead" onSubmit={handleSubmit(onSubmit)}>
 *     ...campos...
 *   </form>
 * </ModalCore>
 */
const ModalCore = ({ trigger, title, children, size = "lg", responsive = false, formId, isLoading, onClose: onCloseProp, onOpen: onOpenProp, isOpen: isOpenProp }) => {
    const { isOpen: isOpenInternal, onOpen, onClose } = useDisclosure();

    const isControlled = isOpenProp !== undefined;
    const isOpen = isControlled ? isOpenProp : isOpenInternal;

    const handleClose = () => {
        if (!isControlled) onClose();
        onCloseProp?.();
    };

    const handleOpen = () => {
        if (!isControlled) onOpen();
        onOpenProp?.();
    };

    return (
        <>
            {trigger?.({ onOpen: handleOpen, onClose: handleClose })}

            <Modal isOpen={isOpen} onClose={handleClose} size={size} scrollBehavior={responsive ? "inside" : "outside"} closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent {...(responsive ? {
                    w: { base: "calc(100vw - 16px)", md: "calc(100vw - 48px)" },
                    maxW: size,
                    my: { base: 2, md: 6 },
                    maxH: { base: "calc(100dvh - 16px)", md: "calc(100dvh - 48px)" },
                } : {})}>
                    <ModalHeader {...(responsive ? { pl: { base: 4, md: 6 }, pr: 12, overflowWrap: "anywhere" } : {})}>
                        <Heading size="md" mt={2} mb={2}>
                            {title}
                        </Heading></ModalHeader>
                    <ModalCloseButton />
                    <ModalBody {...(responsive ? { px: { base: 4, md: 6 }, minH: 0 } : {})}>
                        {typeof children === 'function' ? children({ onClose: handleClose }) : children}
                    </ModalBody>
                    {formId && (
                        <ModalFooter gap={responsive ? 3 : 5} {...(responsive ? { px: { base: 4, md: 6 }, flexWrap: "wrap", flexShrink: 0 } : {})}>
                            <Button variant="ghost" onClick={handleClose}>
                                Fechar
                            </Button>
                            <Button type="submit" form={formId} isLoading={isLoading}>
                                Salvar
                            </Button>
                        </ModalFooter>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
};

export default ModalCore;
